
import { useCallback } from 'react';
import { useEnhancedSubscription } from './useEnhancedSubscription';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/hooks/use-toast';

export type FeatureType = 
  | 'basic_calculator'
  | 'advanced_calculator'
  | 'pdf_reports'
  | 'ai_guidance'
  | 'investment_analysis'
  | 'rental_calculator'
  | 'roi_modeling'
  | 'multi_property'
  | 'branded_reports'
  | 'client_portal'
  | 'crm_integration'
  | 'api_access'
  | 'white_label'
  | 'admin_dashboard';

const FEATURE_TIER_MAP: Record<FeatureType, string[]> = {
  basic_calculator: ['freemium', 'pro', 'investor', 'agent_pro', 'enterprise'],
  advanced_calculator: ['pro', 'investor', 'agent_pro', 'enterprise'],
  pdf_reports: ['pro', 'investor', 'agent_pro', 'enterprise'],
  ai_guidance: ['pro', 'investor', 'agent_pro', 'enterprise'],
  // Investor-tier features are now free for everyone (freemium).
  investment_analysis: ['freemium', 'pro', 'investor', 'agent_pro', 'enterprise'],
  rental_calculator: ['freemium', 'pro', 'investor', 'agent_pro', 'enterprise'],
  roi_modeling: ['freemium', 'pro', 'investor', 'agent_pro', 'enterprise'],
  multi_property: ['freemium', 'pro', 'investor', 'agent_pro', 'enterprise'],
  branded_reports: ['agent_pro', 'enterprise'],
  client_portal: ['agent_pro', 'enterprise'],
  crm_integration: ['agent_pro', 'enterprise'],
  api_access: ['enterprise'],
  white_label: ['enterprise'],
  admin_dashboard: ['enterprise']
};


export const useAdvancedFeatureGating = () => {
  const { subscription, getUpgradePrompt, createCheckoutSession } = useEnhancedSubscription();
  const { hasRole, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  // Debug logging with error handling
  console.log('useAdvancedFeatureGating debug:', {
    hasAdminRole: hasRole(['admin', 'owner']),
    subscription,
    authLoading
  });

  const hasAccess = useCallback((feature: FeatureType): boolean => {
    try {
      // Super admin bypass - admins get access to everything
      if (hasRole(['admin', 'owner'])) {
        console.log(`Admin access granted for advanced feature: ${feature}`);
        return true;
      }

      if (!subscription) return false;
      const allowedTiers = FEATURE_TIER_MAP[feature] || [];
      return allowedTiers.includes(subscription.tier);
    } catch (error) {
      console.error('Error in hasAccess:', error);
      return false;
    }
  }, [subscription, hasRole]);

  const requiresUpgrade = useCallback((feature: FeatureType): boolean => {
    try {
      // Admins never require upgrade
      if (hasRole(['admin', 'owner'])) {
        return false;
      }
      
      return !hasAccess(feature);
    } catch (error) {
      console.error('Error in requiresUpgrade:', error);
      return true;
    }
  }, [hasAccess, hasRole]);

  const handleFeatureAccess = useCallback((
    feature: FeatureType, 
    featureName: string,
    onSuccess?: () => void
  ) => {
    try {
      if (hasAccess(feature)) {
        onSuccess?.();
        return true;
      }

      // Don't show upgrade prompts to admins
      if (hasRole(['admin', 'owner'])) {
        console.log(`Admin override for ${featureName}`);
        onSuccess?.();
        return true;
      }

      const { suggestedTier, reason } = getUpgradePrompt(featureName);
      
      toast({
        title: "Premium Feature",
        description: `${featureName} requires ${suggestedTier} tier. Upgrade to unlock ${reason}.`,
        duration: 5000,
      });

      // Trigger upgrade flow
      createCheckoutSession(suggestedTier);

      return false;
    } catch (error) {
      console.error('Error in handleFeatureAccess:', error);
      return false;
    }
  }, [hasAccess, getUpgradePrompt, createCheckoutSession, hasRole, toast]);

  const getFeatureLimit = useCallback((feature: FeatureType) => {
    try {
      // Admins have no limits
      if (hasRole(['admin', 'owner'])) {
        return null;
      }

      if (!subscription) return null;
      
      // Return usage limits based on tier and feature
      switch (feature) {
        case 'basic_calculator':
          return subscription.tier === 'freemium' ? 5 : null; // 5 per month for freemium
        case 'multi_property':
          // Freemium and investor both capped at 25 properties.
          return subscription.tier === 'freemium' || subscription.tier === 'investor' ? 25 : null;
        default:
          return null;
      }

    } catch (error) {
      console.error('Error in getFeatureLimit:', error);
      return null;
    }
  }, [subscription, hasRole]);

  return {
    hasAccess,
    requiresUpgrade,
    handleFeatureAccess,
    getFeatureLimit,
    currentTier: subscription?.tier || 'freemium'
  };
};
